/**
 * 消息气泡组件
 *
 * - direction=in: 用户来信，靠左对齐，显示用户头像
 * - direction=out: 客服/系统发出，靠右对齐，显示操作者头像
 */

import { Avatar, cn } from '@heroui/react';
import { IconRobot as Bot } from '@tabler/icons-react';
import type { ConversationMessage, ActiveUser } from '../hooks/useWechatApi';

interface MessageBubbleProps {
  message: ConversationMessage;
  /** 发送方用户（用于 direction=in 时显示头像） */
  senderUser: ActiveUser;
}

/** 格式化时间 */
function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const time = date.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (isToday) return time;

  const dateFormatted = date.toLocaleDateString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
  });

  return `${dateFormatted} ${time}`;
}

/** 获取操作者显示名 */
function getOperatorName(msg: ConversationMessage): string {
  if (!msg.operator) return '系统';
  return msg.operator.nickname || msg.operator.username || '客服';
}

/** 获取用户显示名 */
function getUserDisplayName(user: ActiveUser): string {
  if (user.person?.fullName) return user.person.fullName;
  if (user.user?.nickname) return user.user.nickname;
  if (user.user?.username) return user.user.username;
  return user.openid.substring(0, 8);
}

export function MessageBubble({ message, senderUser }: MessageBubbleProps) {
  const isIncoming = message.direction === 'in';

  // 消息内容渲染
  const renderContent = () => {
    if (message.msgType === 'text') {
      return <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>;
    }
    if (message.msgType === 'image') {
      return (
        <div>
          <p className="text-sm text-muted-foreground">[图片消息]</p>
          {message.content && (
            <img src={message.content} alt="图片" className="mt-1 max-w-[200px] rounded" />
          )}
        </div>
      );
    }
    if (message.msgType === 'voice') {
      return <p className="text-sm text-muted-foreground">[语音消息]</p>;
    }
    if (message.msgType === 'video') {
      return <p className="text-sm text-muted-foreground">[视频消息]</p>;
    }
    if (message.msgType === 'location') {
      return <p className="text-sm text-muted-foreground">[位置消息]</p>;
    }
    if (message.msgType === 'link') {
      return <p className="text-sm text-muted-foreground">[链接消息]</p>;
    }
    if (message.msgType === 'event') {
      return (
        <p className="text-sm italic text-muted-foreground">[事件: {message.content}]</p>
      );
    }
    // 默认
    return <p className="text-sm">{message.content || `[${message.msgType}]`}</p>;
  };

  if (isIncoming) {
    // 用户来信 → 靠左
    return (
      <div className="flex max-w-[85%] items-start gap-2">
        {/* 用户头像 */}
        <Avatar className="mt-0.5 size-8 shrink-0" variant="soft">
          {senderUser.user?.avatarUrl ? (
            <Avatar.Image src={senderUser.user.avatarUrl} alt={getUserDisplayName(senderUser)} />
          ) : null}
          <Avatar.Fallback className="bg-primary/10 text-xs text-primary">
            {getUserDisplayName(senderUser).charAt(0)}
          </Avatar.Fallback>
        </Avatar>

        <div>
          <div className={cn('rounded-lg rounded-tl-sm bg-muted px-3 py-2')}>{renderContent()}</div>
          <div className="mt-1 ml-1 text-[10px] text-muted-foreground">{formatTime(message.createdAt)}</div>
        </div>
      </div>
    );
  }

  // 客服/系统发出 → 靠右
  return (
    <div className="ml-auto flex max-w-[85%] flex-row-reverse items-start gap-2">
      {/* 操作者头像 */}
      <Avatar className="mt-0.5 size-8 shrink-0" variant="soft">
        {message.operator?.avatarUrl ? (
          <Avatar.Image src={message.operator.avatarUrl} alt={getOperatorName(message)} />
        ) : null}
        <Avatar.Fallback className="bg-blue-100 text-xs text-blue-600">
          {message.operator ? getOperatorName(message).charAt(0) : <Bot className="size-4" stroke={1.5} />}
        </Avatar.Fallback>
      </Avatar>

      <div>
        <div className={cn('rounded-lg rounded-tr-sm bg-primary px-3 py-2 text-primary-foreground')}>
          {renderContent()}
        </div>
        <div className="mt-1 mr-1 text-right text-[10px] text-muted-foreground">
          {message.operator ? getOperatorName(message) : '自动回复'}
          {' · '}
          {formatTime(message.createdAt)}
          {message.status === 'failed' && <span className="ml-1 text-red-500">发送失败</span>}
        </div>
      </div>
    </div>
  );
}
